import React, { useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import {
  CalendarIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  HeartIcon,
  MoonIcon,
  XCircleIcon,
  TrashIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

const reasonIcons = {
  travel: PaperAirplaneIcon,
  sickness: HeartIcon,
  menstruation: MoonIcon,
  other: CalendarIcon,
};

const reasonColors = {
  travel: 'bg-blue-50 text-blue-700 border-blue-200',
  sickness: 'bg-orange-50 text-orange-700 border-orange-200',
  menstruation: 'bg-purple-50 text-purple-700 border-purple-200',
  other: 'bg-gray-50 text-gray-700 border-gray-200',
};

const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    let date;
    if (typeof dateValue === 'string') {
      // If it's a string, try to parse it as ISO date
      date = parseISO(dateValue);
    } else if (dateValue instanceof Date) {
      // If it's already a Date object, use it directly
      date = dateValue;
    } else {
      // If it's neither, try to create a new Date from it
      date = new Date(dateValue);
    }

    if (!isValid(date)) return 'Invalid date';
    return format(date, 'MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const FastCard = ({ fast, onComplete, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const Icon = reasonIcons[fast.reason] || CalendarIcon;
  const colorClass = reasonColors[fast.reason] || reasonColors.other;
  const isMadeUp = fast.status === 'MADE_UP';
  
  // Calculate suggested makeup dates (including Mondays, Thursdays, and White Days)
  const getSuggestedDates = () => {
    const firstAvailableDate = new Date('2025-04-01'); // First day after Eid when makeup fasts can begin
    const today = new Date();
    
    // Start from April 1st or today, whichever is later
    const startDate = new Date(Math.max(firstAvailableDate, today));
    const nextMonday = new Date(startDate);
    const nextThursday = new Date(startDate);
    
    // Find next Monday
    nextMonday.setDate(nextMonday.getDate() + ((1 + 7 - nextMonday.getDay()) % 7));
    // Find next Thursday
    nextThursday.setDate(nextThursday.getDate() + ((4 + 7 - nextThursday.getDay()) % 7));
    
    // If Monday is before the start date, get next Monday
    if (nextMonday < startDate) {
      nextMonday.setDate(nextMonday.getDate() + 7);
    }
    
    // If Thursday is before the start date, get next Thursday
    if (nextThursday < startDate) {
      nextThursday.setDate(nextThursday.getDate() + 7);
    }

    // Calculate next White Days (13th, 14th, 15th of the lunar month)
    // For simplicity, we'll show the next available set in the Gregorian calendar
    const nextMonth = new Date(startDate);
    nextMonth.setDate(1); // Go to first of current month
    if (startDate.getDate() >= 15) {
      nextMonth.setMonth(nextMonth.getMonth() + 1); // Go to next month if we're past the 15th
    }
    
    const whiteDays = [
      new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 13),
      new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 14),
      new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 15),
    ].filter(date => date >= startDate); // Only include dates after our start date
    
    return {
      monday: nextMonday.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      }),
      thursday: nextThursday.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      }),
      whiteDays: whiteDays.map(date => 
        date.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      )
    };
  };

  const suggestedDates = getSuggestedDates();

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(fast);
    } catch (error) {
      console.error('Error deleting fast:', error);
      alert('Failed to delete fast. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${colorClass} hover:shadow-md relative`}>
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex items-center justify-center p-4 z-10">
          <div className="text-center">
            <p className="text-gray-700 mb-4">Are you sure you want to delete this fast?</p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-col">
        <div className="flex items-start space-x-3">
          <div className="p-2 rounded-full bg-white">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium">
              {formatDate(fast.date)}
            </h3>
            <p className="text-sm capitalize mt-1">
              Reason: {fast.displayReason || fast.reason}
            </p>
            {!isMadeUp && (
              <div className="text-sm mt-2 space-y-2">
                <div>
                  <p className="font-medium text-gray-700">Recommended makeup dates (starting April 1st):</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>{suggestedDates.monday}</li>
                    <li>{suggestedDates.thursday}</li>
                  </ul>
                </div>
                {suggestedDates.whiteDays.length > 0 && (
                  <div>
                    <p className="font-medium text-gray-700">White Days (more rewarding):</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      {suggestedDates.whiteDays.map((date, index) => (
                        <li key={index}>{date}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {fast.notes && (
              <div className="mt-3 bg-white/80 rounded-md p-3 border border-current border-opacity-10">
                <div className="flex items-start space-x-2">
                  <svg className="h-5 w-5 text-current opacity-70 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-current opacity-80 mb-1">Notes</p>
                    <p className="text-sm whitespace-pre-line">
                      {fast.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-current border-opacity-10 flex justify-between items-center">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors duration-200"
            title="Delete fast"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => onComplete(fast)}
            className={`flex items-center justify-center space-x-1 px-4 py-2 rounded-md transition-all duration-200 min-w-[140px] ${
              fast.status === 'MADE_UP'
                ? 'bg-green-100 text-green-700 hover:bg-green-50'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
            title={fast.status === 'MADE_UP' ? 'Undo completion' : 'Mark this fast as completed'}
          >
            {fast.status === 'MADE_UP' ? (
              <>
                <CheckCircleIcon className="w-4 h-4" />
                <span className="text-sm font-medium">Completed</span>
              </>
            ) : (
              <span className="text-sm font-medium">Mark as Complete</span>
            )}
          </button>
        </div>
      </div>
      {fast.status === 'MADE_UP' && fast.completedAt && (
        <div className="mt-2 text-sm text-green-600">
          ✓ Made up on {formatDate(fast.completedAt)}
        </div>
      )}
    </div>
  );
};

export default FastCard; 